import { useGetSchemaDefinition } from 'src/api/rpc/hooks/core/useSchemaDefinitions';
import {
  NO_INPUT_SCHEMA_VALUE,
  type TaskTypeEditorState,
} from './taskTypeAdmin';
import { resolveViewSchemaSource } from './taskTypeViewModel';
import type { MetricFieldOption } from './taskTypeMetricsDraft';
import type {
  ProcedureInputField,
  ProcedureInputSchemaStatus,
} from './procedureParameterBinding';

/**
 * Top-level fields of the input schema selected on the task type editor:
 * `fields` for playbook parameters, `metricFields` (with their types) for
 * metric sum cards.
 */
export function useTaskTypeProcedureInput(editor: TaskTypeEditorState): {
  status: ProcedureInputSchemaStatus;
  fields: ProcedureInputField[];
  metricFields: MetricFieldOption[];
} {
  const selected = editor.inputSchemaDefinitionId;
  const saved = editor.editing?.inputSchema ?? null;
  const shouldFetch =
    selected !== NO_INPUT_SCHEMA_VALUE && saved?.id !== selected;
  const query = useGetSchemaDefinition(shouldFetch ? selected : '');
  const source = resolveViewSchemaSource({
    selectedId: selected,
    noneId: NO_INPUT_SCHEMA_VALUE,
    saved,
    fetched: query.data ?? null,
    fetchFailed: query.isError,
  });
  if (source.status !== 'ready') {
    return { status: source.status, fields: [], metricFields: [] };
  }
  return {
    status: 'ready',
    fields: source.fields.map((field) => ({
      fieldName: field.fieldName,
      label: field.label.trim() || field.fieldName,
    })),
    metricFields: source.fields.map((field) => ({
      fieldName: field.fieldName,
      fieldType: field.fieldType,
      label: field.label.trim() || field.fieldName,
      isMultiple: field.isMultiple ?? false,
    })),
  };
}
